import { useEffect, useRef } from 'react';
import { useStrings } from '@/i18n/Localization';
import styles from '@/components/MessageMenu.module.css';

export interface MessageMenuProps {
  /** Section 14: edit is offered only where the user is permitted to edit. */
  canEdit: boolean;
  canDelete: boolean;
  canCopy: boolean;
  /** Section 06 forbids forwarding out of a protected-content conversation. */
  canForward: boolean;
  /** Pinning belongs to administrators in a group or channel. */
  canPin: boolean;
  pinned: boolean;
  onReply: () => void;
  onCopy: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onForward: () => void;
  onPin: () => void;
  onClose: () => void;
}

/**
 * The Section 14 message menu: reply, copy, edit if permitted, forward from
 * Phase 2, pin, and delete with an explicit scope dialog.
 *
 * Copy and forward are both absent in a protected-content conversation rather
 * than present and refused. Section 06 makes copy "a distinct local action,
 * unavailable in protected-content UI", and a control that never works teaches
 * people to distrust the ones that do.
 *
 * Focus is moved into the menu on open and the Escape key closes it. Without
 * that a keyboard user opening the menu would still be focused on the button
 * behind it, and tabbing would walk into the timeline rather than the actions
 * they just asked for.
 */
export function MessageMenu(props: MessageMenuProps) {
  const { t } = useStrings();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const firstItemRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    firstItemRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        props.onClose();
      }
    };
    const onPointerDown = (event: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        props.onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [props]);

  return (
    <div ref={containerRef} className={styles.menu} role="menu" aria-label={t('message.actions')}>
      <button ref={firstItemRef} type="button" role="menuitem" className={styles.item} onClick={props.onReply}>
        {t('message.reply')}
      </button>
      {props.canCopy ? (
        <button type="button" role="menuitem" className={styles.item} onClick={props.onCopy}>
          {t('message.copy')}
        </button>
      ) : null}
      {props.canForward ? (
        <button type="button" role="menuitem" className={styles.item} onClick={props.onForward}>
          {t('forward.action')}
        </button>
      ) : null}
      {props.canPin ? (
        <button type="button" role="menuitem" className={styles.item} onClick={props.onPin}>
          {props.pinned ? t('pin.remove') : t('pin.action')}
        </button>
      ) : null}
      {props.canEdit ? (
        <button type="button" role="menuitem" className={styles.item} onClick={props.onEdit}>
          {t('message.edit')}
        </button>
      ) : null}
      {props.canDelete ? (
        <button
          type="button"
          role="menuitem"
          className={`${styles.item} ${styles.destructive}`}
          onClick={props.onDelete}
        >
          {t('message.delete')}
        </button>
      ) : null}
    </div>
  );
}
